import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import { adminAPI } from '../../utils/api';

const StatCard = ({ icon, value, label, trend, color }) => (
  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
    <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: color || '#FFF3E0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>{icon}</div>
    <div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: '#1A1A2E', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '10px', color: '#64748B', fontWeight: 600, marginTop: '2px' }}>{label}</div>
      {trend && <div style={{ fontSize: '9px', fontWeight: 700, color: trend.up ? '#1B7F3E' : '#DC2626', marginTop: '2px' }}>{trend.text}</div>}
    </div>
  </div>
);

const AdminDashboardPage = () => {
  const [data, setData] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const { success, error } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [dashRes, pendingRes] = await Promise.all([
        adminAPI.getDashboard(),
        adminAPI.getPending({ limit: 5 })
      ]);
      setData(dashRes.data.data);
      setPending(pendingRes.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id, name) => {
    try {
      await adminAPI.approveStudent(id);
      success(`${name} approved!`);
      loadData();
    } catch { error('Failed to approve.'); }
  };

  const handleReject = async (id, name) => {
    const reason = prompt(`Reason for rejecting ${name}? (optional)`);
    try {
      await adminAPI.rejectStudent(id, reason || '');
      success(`${name} rejected.`);
      loadData();
    } catch { error('Failed to reject.'); }
  };

  if (loading) {
    return (
      <AdminShell title="Dashboard">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
          <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
        </div>
      </AdminShell>
    );
  }

  const stats = data?.students || {};
  const analytics = data?.analytics || {};

  return (
    <AdminShell
      title="Super Admin Dashboard"
      subtitle={`${new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · All systems operational`}
      actions={
        <button
          onClick={() => navigate('/admin/approvals')}
          style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}
        >
          View All Pending ({stats.pending || 0})
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        {/* Stats grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
          <StatCard icon="👤" value={stats.total || 0} label="Total Students" color="#EFF6FF" trend={{ up: true, text: `+${stats.newThisWeek || 0} this week` }} />
          <StatCard icon="⏳" value={stats.pending || 0} label="Pending Approvals" color="#FEF3C7" trend={stats.pending > 0 ? { up: false, text: 'Action needed' } : null} />
          <StatCard icon="✅" value={stats.active || 0} label="Active Students" color="#E6F4EC" />
          <StatCard icon="🚫" value={stats.blocked || 0} label="Blocked" color="#FEE2E2" />
          <StatCard icon="🏫" value={data?.schools?.total || 0} label="Schools" color="#FFF3E0" trend={data?.schools?.pending > 0 ? { up: false, text: `${data.schools.pending} pending` } : null} />
        </div>

        {/* Main grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '16px' }}>

          {/* Pending approvals */}
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800 }}>⏳ Pending Approvals — Action Required</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={async () => {
                    if (window.confirm('Approve ALL pending students?')) {
                      try { await adminAPI.bulkApprove({ ids: pending.map(p => p.id) }); success('All approved!'); loadData(); }
                      catch { error('Bulk approve failed.'); }
                    }
                  }}
                  style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '5px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Bulk Approve All
                </button>
                <span style={{ fontSize: '11px', color: '#FF6B00', fontWeight: 700, cursor: 'pointer' }} onClick={() => navigate('/admin/approvals')}>View all ›</span>
              </div>
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              {['All', 'Individual', 'School'].map(f => (
                <button key={f} style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, border: f === 'All' ? 'none' : '1px solid #E2E8F0', background: f === 'All' ? '#1A3C6E' : 'white', color: f === 'All' ? 'white' : '#64748B', cursor: 'pointer' }}>{f}</button>
              ))}
            </div>

            {/* Student table */}
            {pending.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
                <div style={{ fontWeight: 700 }}>No pending approvals</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Student', 'Class', 'Type', 'Registered', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{ fontSize: '10px', fontWeight: 800, color: '#64748B', textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #E2E8F0', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pending.map(s => (
                    <tr key={s.id} style={{ borderBottom: '1px solid #F7F8FC' }}>
                      <td style={{ padding: '9px 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#E8F0FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 800, color: '#1A3C6E', flexShrink: 0 }}>
                            {s.name?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                          </div>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: 700 }}>{s.name}</div>
                            <div style={{ fontSize: '10px', color: '#64748B' }}>{s.parent?.email || s.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: '12px', padding: '9px 8px' }}>Class {s.class_id}</td>
                      <td style={{ padding: '9px 8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 800, background: s.registration_path === 'school' ? '#FEF3C7' : '#E8F0FB', color: s.registration_path === 'school' ? '#7C2D12' : '#1A3C6E', borderRadius: '20px', padding: '2px 7px' }}>
                          {s.registration_path === 'school' ? 'School' : 'Individual'}
                        </span>
                      </td>
                      <td style={{ fontSize: '11px', color: '#64748B', padding: '9px 8px' }}>
                        {new Date(s.created_at).toLocaleDateString('en-IN')}
                      </td>
                      <td style={{ padding: '9px 8px' }}>
                        <span style={{ fontSize: '10px', fontWeight: 800, background: '#FEF3C7', color: '#7C2D12', borderRadius: '20px', padding: '2px 8px' }}>Pending</span>
                      </td>
                      <td style={{ padding: '9px 8px' }}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button onClick={() => handleApprove(s.id, s.name)} style={{ background: '#1B7F3E', color: 'white', border: 'none', borderRadius: '20px', padding: '3px 9px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>Approve</button>
                          <button onClick={() => handleReject(s.id, s.name)} style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #DC2626', borderRadius: '20px', padding: '3px 8px', fontSize: '10px', fontWeight: 800, cursor: 'pointer' }}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            {/* Analytics */}
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '12px' }}>📊 Platform Analytics</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  { label: 'Avg Mastery Score', value: `${analytics.avgMasteryScore || 0}%`, color: '#FF6B00', pct: analytics.avgMasteryScore || 0 },
                  { label: 'Revision Compliance', value: `${analytics.revisionCompliance || 0}%`, color: '#1B7F3E', pct: analytics.revisionCompliance || 0 },
                ].map(a => (
                  <div key={a.label} style={{ background: '#F7F8FC', borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: '#64748B', marginBottom: '6px' }}>{a.label}</div>
                    <div style={{ background: '#E2E8F0', borderRadius: '10px', height: '6px', marginBottom: '4px' }}>
                      <div style={{ background: a.color, borderRadius: '10px', height: '100%', width: `${a.pct}%` }}></div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 800 }}>{a.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick actions */}
            <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', padding: '14px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, marginBottom: '12px' }}>⚡ Quick Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  { label: 'Manage Banners', path: '/admin/banners', icon: '🖼️', color: '#FFF3E0' },
                  { label: 'Edit Branding & Theme', path: '/admin/branding', icon: '🎨', color: '#E8F0FB' },
                  { label: 'Manage Schools', path: '/admin/schools', icon: '🏫', color: '#E6F4EC' },
                  { label: 'Platform Settings', path: '/admin/settings', icon: '⚙️', color: '#F7F8FC' },
                ].map(a => (
                  <div
                    key={a.path}
                    onClick={() => navigate(a.path)}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #E2E8F0', cursor: 'pointer', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#FF6B00'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#E2E8F0'}
                  >
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: a.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>{a.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: 700 }}>{a.label}</div>
                    <span style={{ marginLeft: 'auto', color: '#64748B', fontSize: '12px' }}>›</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </AdminShell>
  );
};

export default AdminDashboardPage;
